import { useTheme } from '@fluentui/react';
import { useContext } from 'react';
import AppContext from 'App/AppContext';
import {
  IPFontIcon,
  IPLabel,
  IPScrollablePane,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import WarningMessage from 'components/Notification/WarningMessage';
import { globalStackTokensGapMedium, globalStackTokensGapSmall, infoIcon } from 'globalStyles';
import { useTranslation } from 'react-i18next';

export interface IConfirmActionContentProps {
  subText: string;
  confirmText: string;
  enteredConfirmText: string;
  onEnteredConfirmTextChange: (value: string) => void;
  optionalJSX?: JSX.Element;
  optionalJSXHeight?: string | number;
  optionalJSXWidth?: string | number;
  hideWarning?: boolean;
  warning?: JSX.Element | null;
  hideConfirmInput?: boolean;
}

const ConfirmActionContent = ({
  subText,
  confirmText,
  enteredConfirmText,
  onEnteredConfirmTextChange,
  optionalJSX,
  optionalJSXHeight,
  optionalJSXWidth,
  hideWarning,
  warning,
  hideConfirmInput,
}: IConfirmActionContentProps) => {
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const { t } = useTranslation(['translation']);

  const renderConfirmTextLabel = () => (
    <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
      <IPLabel>{t('translation:Dialogs.ConfirmGeneric.ConfirmTextLabel')}</IPLabel>
      <IPTooltipHost
        content={
          <IPText>
            {t('translation:Dialogs.ConfirmGeneric.ConfirmTextLabelInfo', {
              confirmText,
            })}
          </IPText>
        }
        calloutProps={{ beakWidth: 0 }}
      >
        <IPFontIcon
          {...infoIcon}
          style={{ color: theme.semanticColors.bodySubtext, cursor: 'pointer' }}
        />
      </IPTooltipHost>
    </IPStack>
  );

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      {!hideWarning && (
        <IPStack.Item>
          <WarningMessage message={warning ? undefined : t('translation:Dialogs.ConfirmGeneric.Warning')}>
            {warning}
          </WarningMessage>
        </IPStack.Item>
      )}
      {optionalJSX && (
        <IPStack.Item
          grow
          styles={{
            root: {
              marginTop: 0,
              position: 'relative',
              height: optionalJSXHeight,
              width: optionalJSXWidth,
              minHeight: 50,
              maxHeight: '50vh',
            },
          }}
        >
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>{optionalJSX}</IPScrollablePane>
        </IPStack.Item>
      )}
      {!hideConfirmInput && (
        <IPStack.Item>
          <IPTextField
            autoFocus={!appContext.isMobileView}
            onPaste={(e) => {
              e.preventDefault();

              return false;
            }}
            value={enteredConfirmText}
            onRenderLabel={renderConfirmTextLabel}
            placeholder={t('translation:Dialogs.ConfirmGeneric.ConfirmTextPlaceHolder', {
              confirmText,
            })}
            onChange={(_ev, newValue) => onEnteredConfirmTextChange(newValue ?? '')}
          />
        </IPStack.Item>
      )}
      <IPStack.Item>
        <IPText>{subText}</IPText>
      </IPStack.Item>
    </IPStack>
  );
};

export default ConfirmActionContent;
